/**
 * WhsPanel (13-28): the WHS branch of an incident, and a WHS hazard's `HazardType` on the Risk register (FIELDSET
 * §3.3.1's WHS verb, 22 columns; §3.3.4).
 *
 * - **When:** an incident with a WHS branch (`whsBranchReasons`: a worker injured, the WorkSafe question answered, the
 *   Staff business unit, a hazard / near miss, or a WorkSafe decision, D-13-28-6). Otherwise one line, "No WHS branch
 *   on this incident", with "Open the WHS branch anyway" for HR & WHS (their call, never hidden from them). On a Risk
 *   in the `WHS` category, `HazardType` only.
 * - **One question at a time:** the WHS verb's columns in eight numbered groups (`WHS_GROUPS`, asserted against the
 *   allow-list), each with a "Why" disclosure citing its `12.1-OBLIGATIONS.md` rows.
 * - **The injured worker by name** (13.2, D-13.2-A-3 / -P-2): `InjuredWorkerName`, with a name hint. FieldLink's own
 *   address (`InjuredWorkerUPN`) is shown read only beside it, "Reported by the worker themselves (FieldLink): …".
 * - **The deadlines:** `NotificationDueAt` is computed (13-27's `notificationDue`, WorkSafe = `AwareAt`), shown under
 *   `AwareAt` with an override that needs its own reason. Each timed duty shows its "by" day (`whsDayDeadlines`:
 *   forward the claim + 10 days, the certificate, notify a mental injury claim + 3 business days, RTW planning),
 *   amber once passed while its done day is blank (D-13-15-4), with the OB row in "Why".
 * - **The log rows:** "Site preserved / released" (`SitePreservation`, OB-OHS-13…15) and "Return-to-work step" (`RTW`)
 *   through `SiblingComposer`, under the WHS verb.
 * - **Who** (D-13-L-2): HR & WHS. Anyone else with a register role reads it, with "HR & WHS handles the WHS branch".
 * Saves go through `buildRecordVerb('whs', …)` and the Writer: synchronous, a confirm listing every change, the eTag
 * (D-13-L-4). Practice mode: the practice writer stores nothing on SharePoint.
 */

import { useMemo, useState } from 'react';
import VerbForm from '../VerbForm';
import type { VerbBuilder, VerbFormControl } from '../VerbForm';
import SiblingComposer from '../SiblingComposer';
import Lines from '../Lines';
import { Button, Input, TextArea } from '../../ui';
import { fieldOf } from '../../../config/fieldset/registerFields';
import { formatDay, formatInstant, todayMelbourne } from '../../../lib/fieldValues';
import type { DeadlineRule } from '../../../lib/notificationDeadlines';
import { canDoRecordVerb } from '../../../lib/recordAuthority';
import { valueOf } from '../../../lib/recordView';
import { buildRecordVerb, WRITER_OF_VERB } from '../../../lib/recordVerbs';
import { composerKinds } from '../../../lib/siblingComposer';
import type { ComposerPrompt } from '../../../lib/siblingComposer';
import { NOT_RECORDED } from '../../../lib/verbForm';
import type { FormValues } from '../../../lib/verbForm';
import {
  BY_WORD,
  NO_FIXED_DAY,
  PERMITTED_DISTURBANCES,
  WHS_COMPUTED,
  WHS_GROUPS,
  WHS_INPUTS,
  groupStartingAt,
  isWhsRisk,
  whsBranchReasons,
  whsDayStates,
  whsFormDue,
} from '../../../lib/whsPanels';
import type { DayState, WhsGroup } from '../../../lib/whsPanels';
import type { RecordPatch, RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import record from '../record.module.css';
import styles from './WhsPanel.module.css';

const I = 'incidents';
const WHO = 'HR & WHS handles the WHS branch.';
const MIN_REASON = 10;

/** The WHS verb's rows on Incidents (§3.4.1, writer `WHS`): exactly site preservation and return to work. */
const ROW_KINDS = composerKinds(I, WRITER_OF_VERB.whs);
const ROW_BUTTONS: Readonly<Record<string, string>> = {
  SitePreservation: 'Site preserved / released',
  RTW: 'Return-to-work step',
};
if (ROW_KINDS.length !== 2 || !ROW_KINDS.every((k) => k.logKind && ROW_BUTTONS[k.logKind])) {
  throw new Error('WhsPanel: the WHS verb’s §3.4.1 rows are not SitePreservation and RTW');
}

/** OB-OHS-13…15: the site preservation questions (the Act's events and carve-outs; nothing pre-selected). */
const SITE_PROMPTS: readonly ComposerPrompt[] = [
  { key: 'preserved', label: 'Site preserved?', choices: ['Yes', 'No'], required: true, hint: 'OB-OHS-13 · OHSA-VIC-2004-NOTIF s 39(1).' },
  {
    key: 'ended',
    label: 'Inspector arrived, or the inspector’s direction:',
    hint: 'When the inspector arrived, or what they directed and when (OB-OHS-14 · s 39(1)(a)-(b)).',
  },
  {
    key: 'disturbance',
    label: 'Disturbance the Act permits (s 39(2)):',
    choices: PERMITTED_DISTURBANCES,
    hint: 'Only if the site was disturbed; say why below (OB-OHS-15).',
  },
];

const ROW_INTRO: Readonly<Record<string, string>> = {
  SitePreservation: 'One row on the trail, under your name. Preserve the site from the notice until an inspector arrives or directs otherwise.',
  RTW: 'One row on the trail, under your name: a planning step, a consultation, or the arrangements given to the worker (OB-OHS-39 to -43).',
};

const COLUMN_HINT: Readonly<Record<string, string>> = {
  WorkSafeIncidentKind: 'Needed when it is notifiable: the s 37 kind, in the Act’s words.',
  UnderHarmonyControl: 'An assessment: the Act does not say whether a client’s home is under Harmony’s control (OB-OHS-03).',
  // 13.2 (D-13.2-P-2, 13.2-10): the worker by name; support workers have no Harmony account and need no QMS access.
  InjuredWorkerName: 'The worker’s name as it appears in ShiftCare or Harmony AP. That is enough to find them; they don’t need QMS access.',
  AllegationAgainstWorker: 'Shared with triage: whichever saves last is on the trail, under its own name.',
  AllegationFirstHeldOn: 'Shared with triage. The day the allegation was first held.',
  RtwKnowledgeOn: 'The day Harmony knew, or ought reasonably to have known, of the worker’s incapacity.',
};

function text(value: unknown): string {
  return typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);
}

/** FieldLink's own address column (D-13.2-P-2): written only by the worker's own FieldLink report, never an input here. */
const FIELDLINK_UPN = 'InjuredWorkerUPN';

/**
 * 13.2-10: when FieldLink's "I am the injured worker" set the address, it is shown read only beside the name, in words.
 * Nothing on this panel writes or clears it (the Complete panel may clear it, 13.2-09).
 */
function FieldLinkSelfReport({ row }: { row: RecordRow }) {
  const upn = text(row.fields[FIELDLINK_UPN]).trim();
  if (!upn) return null;
  return (
    <p className={record.hint} data-testid="whs-fieldlink-self-report">
      Reported by the worker themselves (FieldLink): {upn}
    </p>
  );
}

/** A rule's citation in small words: the row, the section, the period as the row states it. */
function Why({ rule }: { rule: { obRow: string | null; section: string; period: string; reading: string; note?: string; provisional?: string } }) {
  return (
    <details className={styles.why}>
      <summary>Why</summary>
      <ul>
        <li>
          <span className={styles.cite}>{rule.obRow ?? 'No obligations row'}</span> · {rule.section}: &ldquo;{rule.period}&rdquo;. Read as {rule.reading}.
        </li>
        {rule.note && <li>{rule.note}</li>}
        {rule.provisional && <li>Provisional: {rule.provisional}.</li>}
      </ul>
    </details>
  );
}

/** One timed duty's line: done, overdue (amber), or by. */
function DayLine({ state }: { state: DayState }) {
  const { rule, done, overdue } = state;
  const by = BY_WORD[rule.notice ?? ''] ?? 'Due by';
  if (done) {
    const late = done > rule.dueOn;
    return (
      <>
        <p className={record.hint}>
          Done {formatDay(done)}; {by.toLowerCase()} {formatDay(rule.dueOn)}
          {late ? ', so it was done after the day' : ''}.
        </p>
        <Why rule={rule} />
      </>
    );
  }
  return (
    <>
      {overdue ? (
        <p className={record.notice} role="status">
          <strong>Overdue.</strong> {by} {formatDay(rule.dueOn)}.
        </p>
      ) : (
        <p className={record.hint}>
          <strong>
            {by} {formatDay(rule.dueOn)}
          </strong>
          : {rule.reading}.
        </p>
      )}
      <Why rule={rule} />
    </>
  );
}

/** A group's numbered question and its "Why". */
function GroupHead({ group, index }: { group: WhsGroup; index: number }) {
  return (
    <div className={styles.groupHead}>
      <h4 className={styles.groupTitle}>
        <span className={styles.groupNumber}>{index + 1}.</span>
        {group.question}
      </h4>
      <details className={styles.why}>
        <summary>Why</summary>
        <ul>
          {group.why.map((w) => (
            <li key={w.ob}>
              <span className={styles.cite}>{w.ob}</span> · {w.section}: {w.says}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function RuleLine({ rule }: { rule: DeadlineRule }) {
  return (
    <li>
      {formatInstant(rule.dueAt)}: {rule.reading}.{' '}
      <span className={styles.cite}>
        {rule.obRow ?? 'No obligations row'} · {rule.section}
      </span>
      {rule.provisional ? ` Provisional: ${rule.provisional}.` : ''}
    </li>
  );
}

interface Override {
  open: boolean;
  day: string;
  time: string;
  reason: string;
}
const CLOSED: Override = { open: false, day: '', time: '', reason: '' };

/** `NotificationDueAt` under `AwareAt`: what is stored, what the form gives, and the override (D-13-L-11). */
function DueBlock({ row, values, control, override, setOverride }: { row: RecordRow; values: FormValues; control: VerbFormControl; override: Override; setOverride: (o: Override) => void }) {
  const [today] = useState(() => todayMelbourne(new Date()));
  const due = whsFormDue(row, values);
  const stored = text(row.fields[WHS_COMPUTED]);
  const change = (next: Override) => {
    setOverride(next);
    control.invalidate();
  };
  const refused = control.refusal('dueOverride');
  return (
    <div className={styles.due} aria-live="polite">
      <p className={record.label}>{fieldOf(I, WHS_COMPUTED)?.label ?? 'Notification due'}</p>
      {stored && <p className={record.hint}>On the record: {formatInstant(stored)}.</p>}
      {due.dueAt ? (
        <>
          <p className={styles.dueValue}>{formatInstant(due.dueAt)}</p>
          <p className={record.hint}>
            Worked out from the record when you save, never typed. It is stored when the aware time or the WorkSafe answer changes.
            {due.rules.length > 1 ? ' The earliest is stored.' : ''}
          </p>
          <ul className={record.hint}>
            {due.rules.map((r) => (
              <RuleLine key={r.key} rule={r} />
            ))}
          </ul>
        </>
      ) : due.missing ? (
        <p className={record.hint}>Enter when Harmony became aware to start the WorkSafe clock.</p>
      ) : (
        <p className={record.hint}>No WorkSafe deadline unless the incident is notifiable.</p>
      )}
      {due.dueAt && !override.open && (
        <Button type="button" variant="ghost" size="sm" onClick={() => change({ ...CLOSED, open: true })}>
          Override
        </Button>
      )}
      {due.dueAt && override.open && (
        <div className={styles.override}>
          <p className={record.hint}>A different deadline, for example a time WorkSafe set. It needs its own reason, which goes on the trail.</p>
          <div className={record.dateRow}>
            <Input type="date" aria-label="Overridden deadline: date" value={override.day} className={record.dateInput} onChange={(e) => change({ ...override, day: e.target.value })} />
            <Input type="time" aria-label="Overridden deadline: time" value={override.time} className={record.timeInput} onChange={(e) => change({ ...override, time: e.target.value })} />
            <Button type="button" variant="ghost" size="sm" onClick={() => change({ ...override, day: today })}>
              Today
            </Button>
          </div>
          <label className={record.label} htmlFor="whs-override-reason">
            Why the deadline is different
          </label>
          <TextArea id="whs-override-reason" rows={2} value={override.reason} onChange={(e) => change({ ...override, reason: e.target.value })} />
          <p className={record.hint}>At least {MIN_REASON} characters.</p>
          {refused && <p className={record.refusal}>{refused}</p>}
          <Button type="button" variant="secondary" size="sm" onClick={() => change(CLOSED)}>
            Use the computed time
          </Button>
        </div>
      )}
    </div>
  );
}

/** One column as read only. */
function Fact({ row, column }: { row: RecordRow; column: string }) {
  const spec = fieldOf(row.register, column);
  if (!spec) return null;
  const v = valueOf(spec, row);
  return (
    <div style={{ display: 'contents' }}>
      <dt>{spec.label}</dt>
      <dd>{v.value === null ? <span className={record.blank}>{NOT_RECORDED}</span> : spec.kind === 'textarea' ? <Lines text={v.value} /> : v.value}</dd>
    </div>
  );
}

/** Read only (D-13-L-2): who handles it, each group's answers and the timed duties. */
function ReadOnlyIncident({ row }: { row: RecordRow }) {
  const [today] = useState(() => todayMelbourne(new Date()));
  const states = whsDayStates(row, {}, today);
  return (
    <div className={record.verbForm}>
      <p className={styles.who}>
        <strong>{WHO}</strong> You can read it here.
      </p>
      {WHS_GROUPS.map((g, i) => (
        <div key={g.key}>
          <GroupHead group={g} index={i} />
          <dl className={record.facts}>
            {g.columns.map((c) => (
              <Fact key={c} row={row} column={c} />
            ))}
            {g.key === 'notifiable' && <Fact row={row} column={WHS_COMPUTED} />}
          </dl>
          {g.key === 'worker' && <FieldLinkSelfReport row={row} />}
          {states
            .filter((s) => g.columns.includes(s.rule.notice ?? ''))
            .map((s) => (
              <DayLine key={s.rule.key} state={s} />
            ))}
        </div>
      ))}
    </div>
  );
}

function confirmNote(patch: RecordPatch) {
  return (
    <p className={record.confirmNote}>
      <strong>This records the WHS answers under your name.</strong>
      {WHS_COMPUTED in patch ? ' The notification deadline above is stored with them.' : ''}
    </p>
  );
}

function IncidentWhs({ row, siblings, actor, onSaved, reasons }: PanelProps & { reasons: string[] }) {
  const [override, setOverride] = useState<Override>(CLOSED);
  const [today] = useState(() => todayMelbourne(new Date()));
  const [rowKind, setRowKind] = useState<string | null>(null);
  const fixedKinds = useMemo(() => Object.fromEntries(ROW_KINDS.map((k) => [k.logKind as string, [k]])), []);

  const build: VerbBuilder = (r, input, now) =>
    buildRecordVerb(
      'whs',
      r,
      { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) },
      {
        set: input.set,
        ...(input.reason ? { reason: input.reason } : {}),
        ...(override.open ? { dueOverride: { at: { day: override.day, time: override.time }, reason: override.reason } } : {}),
        auditId: input.auditId,
      },
      now,
    );

  const fieldNote = (column: string, values: FormValues, control: VerbFormControl) => {
    if (column === 'AwareAt') return <DueBlock row={row} values={values} control={control} override={override} setOverride={setOverride} />;
    const state = whsDayStates(row, values, today).find((s) => s.rule.notice === column);
    if (state) return <DayLine state={state} />;
    if (BY_WORD[column]) return <p className={record.hint}>The day it is due shows once its start day is recorded.</p>;
    const fixed = NO_FIXED_DAY[column];
    if (fixed) {
      return (
        <p className={record.hint}>
          {fixed.says} <span className={styles.cite}>{fixed.ob}</span> · {fixed.section}.
        </p>
      );
    }
    if (column === 'InjuredWorkerName') {
      return (
        <>
          <p className={record.hint}>{COLUMN_HINT[column]}</p>
          <FieldLinkSelfReport row={row} />
        </>
      );
    }
    if (COLUMN_HINT[column]) return <p className={record.hint}>{COLUMN_HINT[column]}</p>;
    return null;
  };

  const beforeField = (column: string) => {
    const g = groupStartingAt(column);
    return g ? <GroupHead group={g.group} index={g.index} /> : null;
  };

  return (
    <div className={record.verbForm}>
      <VerbForm
        register={I}
        verb="whs"
        row={row}
        siblings={siblings}
        actor={actor}
        columns={WHS_INPUTS}
        build={build}
        beforeField={beforeField}
        fieldNote={fieldNote}
        confirmNote={confirmNote}
        saveLabel="Save the WHS answers"
        onSaved={onSaved}
        lead={
          <p className={styles.who}>
            <strong>You record the WHS branch, as HR &amp; WHS.</strong>{' '}
            {reasons.length > 0 ? `It opens here because ${reasons.join(', and ')}.` : 'You opened it on an incident with no WHS condition.'} Answer
            what you know; a blank means not yet, never &ldquo;no&rdquo;.
          </p>
        }
      />
      <div className={styles.rowsBlock}>
        <p className={record.label}>On the trail</p>
        <p className={record.hint}>Each is its own row on the trail, under your name. Contact details go in their own fields.</p>
        <div className={styles.rowButtons}>
          {ROW_KINDS.map((k) => (
            <Button
              key={k.key}
              type="button"
              variant="secondary"
              size="sm"
              className={rowKind === k.logKind ? styles.rowPicked : undefined}
              aria-pressed={rowKind === k.logKind}
              onClick={() => setRowKind(rowKind === k.logKind ? null : (k.logKind as string))}
            >
              {ROW_BUTTONS[k.logKind as string]}
            </Button>
          ))}
        </div>
        {rowKind && (
          <SiblingComposer
            key={rowKind}
            row={row}
            actor={actor}
            verb="whs"
            kinds={fixedKinds[rowKind]}
            title={ROW_BUTTONS[rowKind]}
            intro={ROW_INTRO[rowKind]}
            {...(rowKind === 'SitePreservation' ? { prompts: SITE_PROMPTS, textLabel: 'What happened (and why, if it was disturbed)' } : {})}
            onSaved={(saved) => {
              setRowKind(null);
              onSaved(saved);
            }}
          />
        )}
      </div>
    </div>
  );
}

/** A Risk: `HazardType` for a WHS hazard (§3.3.4). */
function RiskHazard({ row, siblings, actor, onSaved, may }: PanelProps & { may: boolean }) {
  const [forced, setForced] = useState(false);
  const whs = isWhsRisk(row);
  if (!whs && !forced) {
    return (
      <div className={`${record.verbForm} ${styles.none}`}>
        <p className={record.hint}>This risk isn&rsquo;t in the WHS category, so no hazard type is needed.</p>
        {may && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setForced(true)}>
            Record a hazard type anyway
          </Button>
        )}
      </div>
    );
  }
  if (!may) {
    return (
      <div className={record.verbForm}>
        <p className={styles.who}>
          <strong>{WHO}</strong>
        </p>
        <dl className={record.facts}>
          <Fact row={row} column="HazardType" />
        </dl>
      </div>
    );
  }
  return (
    <VerbForm
      register="risk"
      verb="whs"
      row={row}
      siblings={siblings}
      actor={actor}
      saveLabel="Save the hazard type"
      onSaved={onSaved}
      lead={<p className={styles.who}>A WHS hazard: record its type, in the register&rsquo;s words.</p>}
    />
  );
}

export default function WhsPanel(props: PanelProps) {
  const { row, actor, readOnly } = props;
  const may = !readOnly && canDoRecordVerb('whs', row, actor).ok;
  const [forced, setForced] = useState(false);
  if (row.register === 'risk') return <RiskHazard {...props} may={may} />;

  const reasons = whsBranchReasons(row);
  if (reasons.length === 0 && !forced) {
    return (
      <div className={`${record.verbForm} ${styles.none}`}>
        <p className={styles.who}>No WHS branch on this incident.</p>
        <p className={record.hint}>Nobody was recorded as a worker injured, it isn&rsquo;t a Staff or hazard incident, and the WorkSafe question is unanswered.</p>
        {may ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setForced(true)}>
            Open the WHS branch anyway
          </Button>
        ) : (
          <p className={record.hint}>{WHO}</p>
        )}
      </div>
    );
  }
  if (!may) return <ReadOnlyIncident row={row} />;
  return <IncidentWhs {...props} reasons={reasons} />;
}
